/**
 * Query Key Factory for React Query
 *
 * Provides a consistent pattern for generating query keys across all modules.
 * This eliminates the need to manually define query keys in each hook file.
 *
 * Usage:
 * ```typescript
 * const poKeys = createQueryKeys<POFilter>('purchaseOrders');
 *
 * // In hooks:
 * useQuery(poKeys.list(filter), () => fetchList(filter));
 * useQuery(poKeys.detail(id), () => fetchById(id));
 * ```
 */

/**
 * Query key structure returned by createQueryKeys
 */
export interface QueryKeys<TFilter = unknown> {
  /** Base key for all queries in this namespace */
  all: readonly string[];
  /** Key for list queries */
  lists: () => readonly string[];
  /** Key for a specific list with filter */
  list: (filter?: TFilter) => readonly (string | TFilter | undefined)[];
  /** Key for detail queries */
  details: () => readonly string[];
  /** Key for a specific detail by ID */
  detail: (id: string) => readonly string[];
}

/**
 * Create a query key factory for a given namespace
 *
 * @param namespace - The base namespace for all query keys (e.g., 'purchaseOrders')
 * @returns Object with methods to generate consistent query keys
 *
 * @example
 * ```typescript
 * const poKeys = createQueryKeys<POFilter>('purchaseOrders');
 *
 * // Usage in hooks:
 * poKeys.all           // ['purchaseOrders']
 * poKeys.lists()       // ['purchaseOrders', 'list']
 * poKeys.list(filter)  // ['purchaseOrders', 'list', { status: 'pending' }]
 * poKeys.details()     // ['purchaseOrders', 'detail']
 * poKeys.detail('123') // ['purchaseOrders', 'detail', '123']
 * ```
 */
export function createQueryKeys<TFilter = unknown>(namespace: string): QueryKeys<TFilter> {
  return {
    all: [namespace] as const,
    lists: () => [namespace, 'list'] as const,
    list: (filter?: TFilter) => [namespace, 'list', filter] as const,
    details: () => [namespace, 'detail'] as const,
    detail: (id: string) => [namespace, 'detail', id] as const,
  };
}

/**
 * Extended query key factory with additional custom keys
 *
 * @param namespace - The base namespace
 * @param customKeys - Additional key generators
 * @returns Extended query keys object
 *
 * @example
 * ```typescript
 * const poKeys = createExtendedQueryKeys<POFilter>('purchaseOrders', {
 *   vendors: () => ['vendors'] as const,
 *   nextNumber: () => ['po', 'nextNumber'] as const,
 * });
 * ```
 */
export function createExtendedQueryKeys<
  TFilter = unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TCustom extends Record<string, (...args: any[]) => readonly unknown[]> = Record<string, never>
>(namespace: string, customKeys: TCustom): QueryKeys<TFilter> & TCustom {
  return {
    ...createQueryKeys<TFilter>(namespace),
    ...customKeys,
  };
}

// ============================================================================
// Pre-defined Query Keys for Common Modules
// ============================================================================

/**
 * Purchase Order query keys
 */
export const purchaseOrderKeys = createExtendedQueryKeys('purchaseOrders', {
  vendors: () => ['vendors'] as const,
  nextNumber: () => ['po', 'nextNumber'] as const,
});

/**
 * Sales Quotation query keys
 */
export const salesQuotationKeys = createExtendedQueryKeys('salesQuotations', {
  customers: () => ['customers'] as const,
  nextNumber: () => ['sq', 'nextNumber'] as const,
});

/**
 * Sales Order query keys
 */
export const salesOrderKeys = createExtendedQueryKeys('salesOrders', {
  customers: () => ['customers'] as const,
  nextNumber: () => ['so', 'nextNumber'] as const,
});

/**
 * Inbound Shipment query keys
 */
export const inboundKeys = createQueryKeys('inboundShipments');

/**
 * MES Manufacturing Order query keys
 */
export const manufacturingOrderKeys = createExtendedQueryKeys('manufacturingOrders', {
  workOrders: (moId: string) => ['manufacturingOrders', moId, 'workOrders'] as const,
  stats: () => ['manufacturingOrders', 'stats'] as const,
});

/**
 * MES Work Order query keys
 */
export const workOrderKeys = createExtendedQueryKeys('workOrders', {
  defects: (woId: string) => ['workOrders', woId, 'defects'] as const,
  checkpoints: (woId: string) => ['workOrders', woId, 'checkpoints'] as const,
  materials: (woId: string) => ['workOrders', woId, 'materials'] as const,
  timeLogs: (woId: string) => ['workOrders', woId, 'timeLogs'] as const,
});

/**
 * BOM query keys
 */
export const bomKeys = createExtendedQueryKeys('bom', {
  products: () => ['bom', 'products'] as const,
  product: (id: string) => ['bom', 'products', id] as const,
  parts: (productId: string) => ['bom', 'products', productId, 'parts'] as const,
});

/**
 * Vendor query keys
 */
export const vendorKeys = createQueryKeys('vendors');

/**
 * Customer query keys
 */
export const customerKeys = createQueryKeys('customers');

/**
 * Kanban query keys
 */
export const kanbanKeys = createExtendedQueryKeys('kanban', {
  boards: () => ['kanban', 'boards'] as const,
  board: (id: string) => ['kanban', 'boards', id] as const,
  tasks: (boardId?: string) => ['kanban', 'tasks', boardId] as const,
});

/**
 * Blog query keys
 */
export const blogKeys = createExtendedQueryKeys('blogs', {
  published: () => ['blogs', 'published'] as const,
  slug: (slug: string) => ['blogs', 'slug', slug] as const,
});

/**
 * TTQS query keys
 */
export const ttqsKeys = createExtendedQueryKeys('ttqs', {
  plans: () => ['ttqs', 'plans'] as const,
  plan: (id: string) => ['ttqs', 'plans', id] as const,
  courses: () => ['ttqs', 'courses'] as const,
  sessions: (courseId?: string) => ['ttqs', 'sessions', courseId] as const,
});
